import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { GridState } from "ag-grid-community";
import { DEFAULT_SOV_VIEW_ID, isSovViewId, SovViewId } from "./sovViews";

const SOV_VIEW_STORAGE_KEY = "sovViewId";

const readStoredViewId = (): SovViewId => {
  const stored = localStorage.getItem(SOV_VIEW_STORAGE_KEY);
  return isSovViewId(stored) ? stored : DEFAULT_SOV_VIEW_ID;
};

// Holds SOV grid UI state across the AddressCard unmount that TabView causes
// on a tab switch: the AG Grid state snapshot plus the group/freeze toggles
// and the selected view. Mounted in LoadedReport (keyed by reportId), so state
// persists across tab switches but is discarded on report change or a return
// to the dashboard. The view choice alone is a user preference and also
// persists in localStorage across reports.
type SovGridStateValue = {
  gridStateRef: React.MutableRefObject<GridState | undefined>;
  expandedGroups: Set<string>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  freezeColumns: boolean;
  setFreezeColumns: React.Dispatch<React.SetStateAction<boolean>>;
  viewId: SovViewId;
  setViewId: (viewId: SovViewId) => void;
};

const SovGridStateContext = createContext<SovGridStateValue | null>(null);

export const SovGridStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const gridStateRef = useRef<GridState | undefined>(undefined);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [freezeColumns, setFreezeColumns] = useState(false);
  const [viewId, setViewIdState] = useState<SovViewId>(readStoredViewId);
  const setViewId = useCallback((next: SovViewId) => {
    localStorage.setItem(SOV_VIEW_STORAGE_KEY, next);
    setViewIdState(next);
  }, []);

  return (
    <SovGridStateContext.Provider
      value={{
        gridStateRef,
        expandedGroups,
        setExpandedGroups,
        freezeColumns,
        setFreezeColumns,
        viewId,
        setViewId,
      }}
    >
      {children}
    </SovGridStateContext.Provider>
  );
};

export const useSovGridState = (): SovGridStateValue => {
  const value = useContext(SovGridStateContext);
  if (!value) {
    throw new Error(
      "useSovGridState must be used within a SovGridStateProvider"
    );
  }
  return value;
};
